<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2'">
    <!-- Loading State -->
    <empty-state
      v-if="loading.fetchDailyMoreRestaurants"
      :title="$t('loading')"
      :loading="true"
    />

    <template v-else>
      <v-row class="mb-2">
        <v-col cols="12" lg="auto">
          <report-meals-select-companies></report-meals-select-companies>
        </v-col>
      </v-row>

      <!-- Empty State -->
      <empty-state
        v-if="companies.length === 0"
        icon="mdi-food-off"
        :title="$t('no_reserved_meals_data')"
        :subtitle="$t('select_date_and_company')"
      />

      <v-data-table
        v-else
        :headers="getMobileHeaders(headers)"
        :items="companies"
        class="striped-table ordera-box-shadow"
        hide-default-footer
        disable-pagination
        mobile-breakpoint="0"
        :loading="loading.fetchDailyMoreRestaurants"
      >
        <template v-slot:item.name="{ item }">
          {{ toLatin(item.name) }}
        </template>
        <template v-slot:item.num="{ index }">
          {{ index + 1 }}
        </template>
        <template v-slot:item.users="{ item }">
          <circle-button
            icon="mdi-account-group"
            color="primary"
            :tooltip="$t('show_employees_who_ordered')"
            @click="openUsersDialog(item.users)"
          />
        </template>
        <template v-slot:body.append>
          <tr>
            <td v-if="!device.isMobile"></td>
            <td style="font-weight: bold">{{ $t("totalU") }}</td>
            <td style="font-weight: bold">
              {{ totalShift(companies, "shift_one") }}
            </td>
            <td style="font-weight: bold">
              {{ totalShift(companies, "shift_two") }}
            </td>
            <td style="font-weight: bold">
              {{ totalShift(companies, "shift_three") }}
            </td>
            <td style="font-weight: bold">
              {{ totalShift(companies, "shift_four") }}
            </td>
            <td></td>
            <td style="font-weight: bold">
              {{ totalShift(companies, "total") }}
            </td>
          </tr>
        </template>
      </v-data-table>

      <fullscreen-dialog
        v-model="usersDialog.open"
        :title="$t('users')"
        icon="mdi-account-group"
        color="primary"
        hide-footer
      >
        <text-field
          v-model="usersDialog.search"
          :label="$t('search')"
          kind="search"
          hide-details
          class="mb-4"
          color="white"
        ></text-field>

        <v-row v-if="filteredUsers.length > 0">
          <v-col
            v-for="(user, idx) in filteredUsers"
            :key="idx"
            cols="12"
            sm="6"
            md="4"
            lg="3"
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-card outlined hover class="user-card" v-bind="attrs" v-on="on">
                  <v-card-text class="pa-3">
                    <div class="d-flex align-center">
                      <v-avatar size="48" color="primary" class="mr-3">
                        <v-img v-if="user.avatar" :src="user.avatar"></v-img>
                        <span v-else class="white--text text-h6">
                          {{ (user.name || "").charAt(0).toUpperCase() }}
                        </span>
                      </v-avatar>
                      <div class="flex-grow-1">
                        <div class="font-weight-medium text-subtitle-2">
                          {{ user.name + " " + user.lastname }}
                        </div>
                        <div v-if="user.email" class="text-caption grey--text">
                          {{ user.email }}
                        </div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </template>
              <span>ID: {{ user.id }}</span>
            </v-tooltip>
          </v-col>
        </v-row>

        <empty-state
          v-else
          icon="mdi-account-off"
          :title="$t('no_users')"
          :subtitle="usersDialog.search ? $t('no_results_found') : $t('no_users_found')"
        />
      </fullscreen-dialog>
    </template>
  </div>
</template>

<script>
import reportService from "@/services/report";
import ReportMealsSelectCompanies from "@/components/report/meals/fields/ReportMealsSelectCompaniesField.vue";
import EmptyState from "@/components/global/EmptyState.vue";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";
import hasMobileHeaders from "@/mixins/report/hasMobileHeaders";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";

export default {
  mixins: [hasMobileHeaders, hasPrinting, hasExcel],
  components: {
    ReportMealsSelectCompanies,
    EmptyState,
    FullscreenDialog,
  },
  data: () => ({
    companies: [],
    loading: {
      fetchDailyMoreRestaurants: false,
    },
    usersDialog: {
      open: false,
      items: [],
      search: "",
    },
  }),
  methods: {
    openUsersDialog(users) {
      this.usersDialog.items = users || [];
      this.usersDialog.search = "";
      this.usersDialog.open = true;
    },
    async fetchDailyMoreRestaurants() {
      if (this.loading.fetchDailyMoreRestaurants) {
        console.warn(
          "Fetch daily more restaurants operation already in progress, skipping duplicate request"
        );
        return;
      }
      this.loading.fetchDailyMoreRestaurants = true;

      try {
        const companyIds = this.selectedMealCompanies.map((c) => c.id);

        if (companyIds.length === 0) {
          this.companies = [];
          return;
        }

        const params = {
          date_from: this.filters.dates.from,
          date_to: this.filters.dates.to,
          company_ids: companyIds.join(","),
        };

        const response = await reportService.getReservedMealsV2(
          this.$store.state.restaurant.id,
          params
        );

        this.companies = response.data;
      } catch (error) {
        console.error("Error fetching reserved meals:", error);
        this.companies = [];
      } finally {
        this.loading.fetchDailyMoreRestaurants = false;
      }
    },
    totalShift(companies, shiftKey) {
      return companies.reduce((acc, company) => acc + (company[shiftKey] || 0), 0);
    },
    async excelReload() {
      const itemsForExcel = this.companies.map((company, index) => ({
        num: index + 1,
        name: this.toLatin(company.name),
        shift_one: company.shift_one,
        shift_two: company.shift_two,
        shift_three: company.shift_three,
        shift_four: company.shift_four,
        total: company.total,
      }));

      // Add totals row
      itemsForExcel.push({
        num: "",
        name: this.$t("totalU"),
        shift_one: this.totalShift(this.companies, "shift_one"),
        shift_two: this.totalShift(this.companies, "shift_two"),
        shift_three: this.totalShift(this.companies, "shift_three"),
        shift_four: this.totalShift(this.companies, "shift_four"),
        total: this.totalShift(this.companies, "total"),
      });

      this.downloadExcel({
        fileName: [this.$t("report"), this.$t("reserved_meals")].join(" ") + ".xlsx",
        sheets: [
          {
            name: this.$t("reserved_meals"),
            headers: this.headers,
            items: itemsForExcel,
            title: [this.$t("report"), this.$t("reserved_meals")].join(" "),
            subtitle: this.subtitle,
          },
          {
            name: this.$t("companies"),
            headers: [
              { text: this.$t("id"), value: "id" },
              { text: this.$t("company"), value: "name" },
            ],
            items: this.selectedMealCompanies,
          },
        ],
      });
    },
  },
  computed: {
    filteredUsers() {
      if (!this.usersDialog.search) {
        return this.usersDialog.items || [];
      }
      const search = this.usersDialog.search.toLowerCase();
      return (this.usersDialog.items || []).filter((user) => {
        const fullName = `${user.name} ${user.lastname}`.toLowerCase();
        const email = (user.email || "").toLowerCase();
        return fullName.includes(search) || email.includes(search);
      });
    },
    selectedMealCompanies() {
      return this.$store.state.report.selectedMealCompanies || [];
    },
    filters() {
      return this.$store.state.report.filters;
    },
    subtitle() {
      const from = this.filters.dates.from;
      const to = this.filters.dates.to;
      return from === to
        ? this.localeDate(from)
        : `${this.localeDate(from)} - ${this.localeDate(to)}`;
    },
    headers() {
      return [
        { text: this.$t("r_b"), value: "num" },
        { text: this.$t("company"), value: "name" },
        { text: this.$t("1st_shift"), value: "shift_one" },
        { text: this.$t("2nd_shift"), value: "shift_two" },
        { text: this.$t("3rd_shift"), value: "shift_three" },
        { text: this.$t("4th_shift"), value: "shift_four" },
        { text: "Radnici", value: "users" },
        { text: this.$t("total"), value: "total" },
      ];
    },
  },
  watch: {
    isPrinting() {
      if (this.isPrinting) {
        const itemsForPrint = [
          ...this.companies.map((company, index) => ({
            num: index + 1,
            name: this.toLatin(company.name),
            shift_one: company.shift_one,
            shift_two: company.shift_two,
            shift_three: company.shift_three,
            shift_four: company.shift_four,
            total: company.total,
          })),
          {
            num: "",
            name: this.$t("totalU"),
            shift_one: this.totalShift(this.companies, "shift_one"),
            shift_two: this.totalShift(this.companies, "shift_two"),
            shift_three: this.totalShift(this.companies, "shift_three"),
            shift_four: this.totalShift(this.companies, "shift_four"),
            total: this.totalShift(this.companies, "total"),
          },
        ];

        this.printDocument({
          tables: [{ headers: this.headers, items: itemsForPrint, showCaption: true }],
          title: [this.$t("report"), this.$t("r_o")].join(": "),
          subtitle: this.subtitle,
          hideDatetime: true,
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        this.excelReload();
      }
    },
    "filters.dates": {
      handler() {
        this.fetchDailyMoreRestaurants();
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>

<style scoped>
.user-card {
  transition: all 0.2s ease;
  height: 100%;
}

.user-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12) !important;
}
</style>
